// context/CartContext.js
import React, { createContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import api from '../api/axiosInstance';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [userId, setUserId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const cartIconRef = useRef(null);
  const returnPathRef = useRef(window.location.pathname);

  useEffect(() => {
    const checkUserAuth = () => {
      const storedUserId = localStorage.getItem("userId");
      setUserId(storedUserId);
    };
    checkUserAuth();
    window.addEventListener("storage", checkUserAuth);
    window.addEventListener("popstate", checkUserAuth);
    return () => {
      window.removeEventListener("storage", checkUserAuth);
      window.removeEventListener("popstate", checkUserAuth);
    };
  }, []);

  const fetchCartFromBackend = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await api.get(`/cart/${userId}`);
      setCart(data); // data debe ser array con items {id, nombreProducto, etc.}
    } catch (error) {
      console.error("❌ Error obteniendo carrito:", error);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchCartFromBackend();
      setShowPopup(false);
    } else {
      setCart([]);
    }
  }, [userId, fetchCartFromBackend]);

  const addToCart = async (product, quantity = 1) => {
    const currentUserId = localStorage.getItem("userId");
    if (!currentUserId) {
      returnPathRef.current = window.location.pathname;
      setShowPopup(true);
      return;
    }
    try {
      // Asegúrate de que el ID del producto sea un número
      const productId = Number(product.id);
  
      // Busca si el producto ya existe en el carrito
      const existingItemIndex = cart.findIndex(item => Number(item.id) === productId);
  
      let updatedCart;
      if (existingItemIndex !== -1) {
        // Si existe, actualiza la cantidad
        updatedCart = [...cart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + quantity
        };
      } else {
        // Si no existe, agrégalo con la cantidad especificada
        updatedCart = [...cart, { ...product, id: productId, quantity }];
      }
  
      setCart(updatedCart);
  
      await api.post("/cart/add", {
        userId: currentUserId,
        productId: productId,
        quantity,
      });
    } catch (error) {
      console.error("Error agregando al carrito:", error);
    }
  };
  
  const removeFromCart = async (productId) => {
    const currentUserId = localStorage.getItem("userId");
    if (!currentUserId) return;
    try {
      const updatedCart = cart.filter((item) => item.id !== productId);
      setCart(updatedCart);
      await api.delete(`/cart/remove/${currentUserId}/${productId}`);
    } catch (error) {
      console.error("Error eliminando producto del carrito:", error);
    }
  };

  const decreaseQuantity = async (productId) => {
    const currentUserId = localStorage.getItem("userId");
    if (!currentUserId) return;
    try {
      await api.put("/cart/update", {
        userId: currentUserId,
        productId,
        quantity: -1,
      });
      const updatedCart = cart
        .map((item) =>
          item.id === productId ? { ...item, quantity: item.quantity - 1 } : item
        )
        .filter((item) => item.quantity > 0);
      setCart(updatedCart);
    } catch (error) {
      console.error("Error reduciendo cantidad del producto:", error);
    }
  };

  const clearCart = async () => {
    const currentUserId = localStorage.getItem("userId");
    setCart([]);
    if (currentUserId) {
      try {
        await api.delete(`/cart/clear/${currentUserId}`);
      } catch (error) {
        console.error("Error al limpiar el carrito:", error);
      }
    }
  };

  const handleLoginRedirect = () => {
    setShowPopup(false);
    localStorage.setItem("returnTo", returnPathRef.current);
    window.location.href = "/account/signin";
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        decreaseQuantity,
        clearCart,
        cartIconRef,
        showPopup,
        setShowPopup,
        fetchCartFromBackend,
      }}
    >
      {children}
      <Modal show={showPopup} onHide={() => setShowPopup(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Iniciar Sesión</Modal.Title>
        </Modal.Header>
        <Modal.Body>Debes iniciar sesión para agregar productos al carrito.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPopup(false)}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleLoginRedirect}>
            Iniciar Sesión
          </Button>
        </Modal.Footer>
      </Modal>
    </CartContext.Provider>
  );
};

export default CartProvider;
